import tippy from 'tippy.js'

// v-tooltip="'plain text'" renders escaped text (safe for dynamic data).
// v-tooltip="{ content: html, allowHTML: true }" opts into HTML rendering —
// reserve it for trusted, static markup.
// v-tooltip="{ content, theme: 'name' }" adds a tippy theme on top of 'light'.
// Style the theme with .tippy-box[data-theme~='name'].
//
// Accessibility contract:
// - focus on the element (tippy's own trigger) OR any focusable descendant
//   (delegated below — many hints sit on wrapper divs/labels around inputs,
//   selects and datepickers) shows the tip; the focused control gets the
//   popper id APPENDED to its aria-describedby while the tip is visible,
//   preserving any pre-existing description ids.
// - for purely informational hosts (no focusable content, not a control or
//   label), plain-text content is mirrored into an sr-only span so it is in
//   the accessibility tree. Controls/labels are skipped — hidden text there
//   would pollute their accessible names.
const themeOf = (extra) => (extra ? `light ${extra}` : 'light')

function parseBinding(value) {
  if (value && typeof value === 'object') {
    return { content: value.content, allowHTML: value.allowHTML === true, theme: themeOf(value.theme) }
  }
  return { content: value, allowHTML: false, theme: themeOf() }
}

const plainText = (value) => (typeof value === 'string' && !value.includes('<') ? value : '')

const INTERACTIVE_SELECTOR = 'button, a, [href], input, select, textarea, label, [tabindex]'

const isInformationalHost = (el) => !el.matches(INTERACTIVE_SELECTOR) &&
  !el.querySelector(INTERACTIVE_SELECTOR) && !el.closest('button, a, label')

const appendToken = (el, id) => {
  const tokens = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)
  if (!tokens.includes(id)) tokens.push(id)
  el.setAttribute('aria-describedby', tokens.join(' '))
}

const removeToken = (el, id) => {
  const tokens = (el.getAttribute('aria-describedby') || '').split(/\s+/)
    .filter(Boolean).filter((token) => token !== id)
  if (tokens.length) {
    el.setAttribute('aria-describedby', tokens.join(' '))
  } else {
    el.removeAttribute('aria-describedby')
  }
}

export default {
  mounted(el, binding, vnode) {
    const { content, allowHTML, theme } = parseBinding(binding.value)
    vnode.tippy = tippy(el, {
      theme,
      maxWidth: 400,
      content,
      trigger: 'mouseenter focus',
      touch: ['hold', 500],
      allowHTML,
      onShow(instance) {
        // Don't show tooltip if content is empty, null, or undefined
        if (!instance.props.content) return false

        const tippyContent = instance.popper.querySelector('.tippy-content')
        if (tippyContent) {
          tippyContent.classList.add('wrap-anywhere')
        }
      }
    })

    let srText = null
    if (isInformationalHost(el)) {
      srText = document.createElement('span')
      srText.className = 'sr-only'
      srText.textContent = plainText(content)
      el.appendChild(srText)
      el._tooltipSrText = srText
    }

    // Delegated focus: tippy only reacts to focus on the reference itself.
    const onFocusIn = (event) => {
      const instance = el._tippy
      if (!instance || event.target === el) return
      instance.show()
      if (instance.state.isVisible) appendToken(event.target, instance.popper.id)
    }
    const onFocusOut = (event) => {
      const instance = el._tippy
      if (!instance || event.target === el) return
      if (instance.popper?.id) removeToken(event.target, instance.popper.id)
      if (!el.contains(event.relatedTarget)) instance.hide()
    }
    el.addEventListener('focusin', onFocusIn)
    el.addEventListener('focusout', onFocusOut)

    el._tooltipCleanup = () => {
      el.removeEventListener('focusin', onFocusIn)
      el.removeEventListener('focusout', onFocusOut)
      srText?.remove()
    }
  },
  updated(el, binding, vnode, oldVnode) {
    vnode.tippy = oldVnode.tippy
    const { content, allowHTML, theme } = parseBinding(binding.value)
    vnode.tippy.setProps({ content, allowHTML, theme })
    if (el._tooltipSrText) el._tooltipSrText.textContent = plainText(content)
  },
  unmounted(el, _, vnode) {
    vnode.tippy.destroy()
    el._tooltipCleanup?.()
  }
}
